// Google Analytics 4 Configuration
// Replace 'GA_MEASUREMENT_ID' with your actual measurement ID from Google Analytics

export const GA_MEASUREMENT_ID = 'G-975PJL49WV'; // Replace with your actual ID

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window !== 'undefined') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

// Track page views
export const trackPageView = (url, title) => {
  if (typeof window !== 'undefined') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: title,
      page_location: url,
    });
  }
};

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (typeof window !== 'undefined') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track user interactions
export const trackButtonClick = (buttonName, section) => {
  trackEvent('click', 'engagement', `${section}_${buttonName}`);
};

// Track form submissions
export const trackFormSubmission = (formName) => {
  trackEvent('submit', 'form', formName);
};

// Track downloads (if you have resume download)
export const trackDownload = (fileName) => {
  trackEvent('download', 'file', fileName);
};

// Session tracking storage
let visitorSession = null;
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

// Track portfolio visits with session-based notifications
export const trackPortfolioVisit = (section = 'home') => {
  trackEvent('portfolio_visit', 'engagement', section);
  
  // Track additional engagement metrics
  trackEvent('page_view', 'navigation', section, 1);
  
  // Track visitor information (anonymous)
  if (typeof window !== 'undefined') {
    const currentTime = new Date();
    const visitorInfo = {
      timestamp: currentTime.toISOString(),
      section: section,
      userAgent: navigator.userAgent,
      language: navigator.language,
      referrer: document.referrer || 'direct',
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    // Enrich with company/IP data (async, non-blocking)
    enrichVisitorInfo(visitorInfo).then(enrichedInfo => {
      // Send enhanced tracking data with company info
      window.gtag('event', 'enhanced_visit', {
        event_category: 'visitor_tracking',
        event_label: section,
        custom_parameters: enrichedInfo
      });
      
      // Handle session-based notifications with enriched data
      handleSessionTracking(enrichedInfo, currentTime);
    }).catch(() => {
      // Fallback: send without enrichment if API fails
      window.gtag('event', 'enhanced_visit', {
        event_category: 'visitor_tracking',
        event_label: section,
        custom_parameters: visitorInfo
      });
      handleSessionTracking(visitorInfo, currentTime);
    });
  }
};

// Fetch company/organization info from IP intelligence API
const enrichVisitorInfo = async (visitorInfo) => {
  // Check cache first (sessionStorage for current session)
  const cacheKey = 'visitorCompanyInfo';
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Cache expires after 24 hours
      if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return { ...visitorInfo, ...parsed.data };
      }
    }
  } catch (e) {
    // Ignore cache errors
  }

  // Fetch from IP API (using ipapi.co - 1000 free requests/day)
  // Alternative: ipinfo.io (50k free/month), abstractapi.com
  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    
    if (!response.ok) throw new Error('IP API request failed');
    
    const data = await response.json();
    
    const companyInfo = {
      ip: data.ip,
      city: data.city,
      region: data.region,
      country: data.country_name,
      countryCode: data.country_code,
      isp: data.org || data.isp,
      organization: data.org,
      asn: data.asn,
      timezone: data.timezone,
      latitude: data.latitude,
      longitude: data.longitude,
      isEU: data.in_eu,
      companyName: data.org || 'Unknown',
      isLikelyBusiness: !!(data.org && !/residential|dynamic|pool|dialup|cable|fiber|mobile|wireless/i.test(data.org))
    };
    
    // Cache the result
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        data: companyInfo
      }));
    } catch (e) {
      // Ignore cache write errors
    }
    
    return { ...visitorInfo, ...companyInfo };
  } catch (error) {
    console.log('Company enrichment failed, using basic info:', error.message);
    return {
      ...visitorInfo,
      companyName: 'Unknown',
      isp: 'Unknown',
      city: 'Unknown',
      country: 'Unknown',
      organization: 'Unknown'
    };
  }
};

// Handle session-based tracking and notifications
const handleSessionTracking = (visitorInfo, currentTime) => {
  const sessionId = getOrCreateSessionId();
  
  // Check if this is a new session or continuation of existing session
  if (!visitorSession || isNewSession(currentTime)) {
    // New session - initialize tracking
    visitorSession = {
      sessionId: sessionId,
      startTime: currentTime,
      lastActivity: currentTime,
      visitedPages: [visitorInfo.section],
      visitorInfo: visitorInfo,
      notificationSent: false
    };
    
    console.log('🎯 New visitor session started:', sessionId);
    
    // Send initial notification for new visitor
    if (process.env.NODE_ENV === 'production' || true) { // Allow testing in dev
      console.log('📧 Sending session notification...');
      sendSessionNotification(visitorSession);
      visitorSession.notificationSent = true;
    }
  } else {
    // Existing session - update activity
    visitorSession.lastActivity = currentTime;
    if (!visitorSession.visitedPages.includes(visitorInfo.section)) {
      visitorSession.visitedPages.push(visitorInfo.section);
      console.log('📄 Added page to session:', visitorInfo.section);
      console.log('🛤️  Session journey so far:', visitorSession.visitedPages);
    }
  }
  
  // Store session in localStorage for persistence across page loads
  try {
    localStorage.setItem('portfolioSession', JSON.stringify({
      sessionId: visitorSession.sessionId,
      startTime: visitorSession.startTime.toISOString(),
      lastActivity: visitorSession.lastActivity.toISOString(),
      visitedPages: visitorSession.visitedPages,
      notificationSent: visitorSession.notificationSent
    }));
  } catch (e) {
    console.log('localStorage not available');
  }
};

// Generate or retrieve session ID
const getOrCreateSessionId = () => {
  try {
    const stored = localStorage.getItem('portfolioSession');
    if (stored) {
      const session = JSON.parse(stored);
      const lastActivity = new Date(session.lastActivity);
      const now = new Date();
      
      // If last activity was within session duration, reuse session
      if (now - lastActivity < SESSION_DURATION) {
        return session.sessionId;
      }
    }
  } catch (e) {
    console.log('Error reading session from localStorage');
  }
  
  // Generate new session ID
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Check if this should be considered a new session
const isNewSession = (currentTime) => {
  if (!visitorSession) return true;
  
  const timeSinceLastActivity = currentTime - visitorSession.lastActivity;
  return timeSinceLastActivity > SESSION_DURATION;
};

// Send session-based notification
const sendSessionNotification = async (session) => {
  try {
    console.log('🎯 Sending session notification for session:', session.sessionId);
    
    // Google Apps Script webhook URL
    const webhookUrl = 'https://script.google.com/macros/s/AKfycbxeDbpGysZJdfuun0RtRS7RMHBgfS4euywFvdVn-FYuTINqZa0xMSiNhFZHTZsKwjv-pg/exec';
    
    // Build enriched session data with company info
    const visitorInfo = session.visitorInfo;
    const companyName = visitorInfo.companyName || visitorInfo.organization || 'Unknown';
    const isBusiness = visitorInfo.isLikelyBusiness || false;
    const location = `${visitorInfo.city || 'Unknown'}, ${visitorInfo.country || 'Unknown'}`;
    const isp = visitorInfo.isp || visitorInfo.organization || 'Unknown';
    
    const sessionData = {
      message: `🎯 New visitor exploring your portfolio!`,
      sessionId: session.sessionId,
      startTime: session.startTime.toISOString(),
      initialPage: session.visitedPages[0],
      totalPages: session.visitedPages.length,
      visitedPages: session.visitedPages.join(' → '),
      referrer: visitorInfo.referrer,
      device: getDeviceInfo(visitorInfo.userAgent),
      timezone: visitorInfo.timezone,
      language: visitorInfo.language,
      screenResolution: visitorInfo.screenResolution,
      portfolioUrl: window.location.origin,
      isSessionSummary: true,
      // Company/location enrichment
      companyName: companyName,
      isBusinessVisitor: isBusiness,
      location: location,
      city: visitorInfo.city,
      country: visitorInfo.country,
      isp: isp,
      organization: visitorInfo.organization,
      ip: visitorInfo.ip,
      asn: visitorInfo.asn
    };
    
    console.log('📤 Sending session data:', sessionData);
    
    // Method 1: Try POST request first
    try {
      await fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors', // This bypasses CORS but limits response access
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sessionData)
      });
      
      console.log('✅ Session webhook request sent (no-cors mode)');
      
    } catch (fetchError) {
      console.log('📡 POST failed, trying GET method...', fetchError);
      
      // Method 2: Fallback to GET request with URL parameters
      const params = new URLSearchParams({
        sessionId: session.sessionId,
        startTime: session.startTime.toISOString(),
        pages: session.visitedPages.join(','),
        referrer: visitorInfo.referrer,
        device: getDeviceInfo(visitorInfo.userAgent),
        timezone: visitorInfo.timezone,
        language: visitorInfo.language,
        companyName: companyName,
        isBusinessVisitor: isBusiness.toString(),
        location: location,
        isp: isp,
        isSessionSummary: 'true'
      });
      
      const getUrl = `${webhookUrl}?${params.toString()}`;
      
      // Use image beacon method (always works)
      const img = new Image();
      img.onload = () => console.log('✅ Session image beacon notification sent');
      img.onerror = () => console.log('❌ Session image beacon failed');
      img.src = getUrl;
    }
    
  } catch (error) {
    console.error('❌ Session notification failed:', error);
  }
};

// Helper function to extract device information
const getDeviceInfo = (userAgent) => {
  if (/mobile/i.test(userAgent)) return 'Mobile';
  if (/tablet/i.test(userAgent)) return 'Tablet';
  return 'Desktop';
};

// Track time spent on sections
export const trackTimeOnSection = (section, timeSpent) => {
  trackEvent('time_on_section', 'engagement', section, Math.round(timeSpent));
};

// Track scroll depth
export const trackScrollDepth = (depth, section = 'unknown') => {
  const milestones = [25, 50, 75, 100];
  const milestone = milestones.find(m => depth >= m && depth < m + 25) || 100;
  trackEvent('scroll_depth', 'engagement', `${section}_${milestone}%`, milestone);
};

// Track project interactions
export const trackProjectInteraction = (projectName, action) => {
  trackEvent('project_interaction', 'portfolio', `${projectName}_${action}`);
};

// Track contact form engagement
export const trackContactEngagement = (action) => {
  trackEvent('contact_engagement', 'leads', action);
};

// Track navigation clicks
export const trackNavigation = (destination) => {
  trackEvent('navigation', 'engagement', destination);
};

// Track resume/CV downloads
export const trackResumeDownload = (source) => {
  trackEvent('resume_download', 'conversion', source);
};

// Track certification views
export const trackCertificationView = (certName) => {
  trackEvent('certification_view', 'education', certName);
};

// Track work experience engagement
export const trackWorkExperience = (company, action) => {
  trackEvent('work_experience', 'portfolio', `${company}_${action}`);
};

// Track social media clicks
export const trackSocialClick = (platform) => {
  trackEvent('social_click', 'engagement', platform);
};

// Track about section engagement
export const trackAboutEngagement = (action) => {
  trackEvent('about_engagement', 'portfolio', action);
};
